import { useState } from "react";
import { Alert, Button, Card, Select, Space, Spin, Tooltip } from "antd";
import { DownloadOutlined, LeftOutlined, PlusOutlined, RightOutlined, UploadOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { AUDIT_TYPES, planState, type AuditDto } from "@audit-plan/shared";
import { useAuth } from "../auth/AuthContext";
import { ApiError, download } from "../api/client";
import { usePeople } from "../api/people";
import { useConfig, usePlan, useShowRisk } from "../api/plan";
import { Kpi, PageHead, kpiGrid, kpiGridClass } from "../components/dashboard/Kpi";
import { PlanCalendar, PlanLegend } from "../components/plan/PlanCalendar";
import { CoverageTable } from "../components/plan/CoverageTable";
import { ImportPlanModal } from "../components/plan/ImportPlanModal";
import { AuditFormModal } from "../components/audit/AuditFormModal";
import { FilterBar, PageActions } from "../components/FilterBar";
import { useIsMobile } from "../components/useIsMobile";

export default function PlanPage() {
  const { t } = useTranslation();
  const showRisk = useShowRisk();
  const { can } = useAuth();
  const nav = useNavigate();
  const mobile = useIsMobile();
  // anno nell'URL (?year=2027): piani anche per gli anni futuri (AUD-F2.18)
  const [sp, setSp] = useSearchParams();
  const thisYear = dayjs().year();
  const yParam = Number(sp.get("year"));
  const year = Number.isInteger(yParam) && yParam >= 2000 && yParam <= 2100 ? yParam : thisYear;
  const setYear = (y: number) => setSp((p) => { const n = new URLSearchParams(p); if (y === thisYear) n.delete("year"); else n.set("year", String(y)); return n; }, { replace: true });
  const [aud, setAud] = useState<string>();
  const [type, setType] = useState<string>();
  const [ps, setPs] = useState<string>();
  const [form, setForm] = useState<{ date?: string; supplierId?: string } | null>(null);
  const [imp, setImp] = useState(false);
  const people = usePeople();
  const plan = usePlan(year);
  const cfg = useConfig();

  if (!can("plan.view")) return <Alert type="warning" showIcon message={t("notAllowed")} />;
  const canCreate = can("audit.create");
  const years = Array.from({ length: Math.max(thisYear + 5, year) - Math.min(thisYear - 3, year) + 1 }, (_, i) => Math.min(thisYear - 3, year) + i);
  if (plan.isLoading) return <Spin style={{ display: "block", margin: 48 }} />;
  if (plan.error || !plan.data) return <Alert type="error" showIcon message={plan.error instanceof ApiError ? plan.error.message : t("a_loadFail")} />;

  const all = plan.data.audits;
  // persona (AUD-F8.4): auditor o audit manager dell'audit. Prima il filtro guardava solo l'auditor e le persone con ruolo
  // admin non c'erano: in prod i 27 audit pianificati hanno Matteo auditor e Matilda manager, filtrando per Matilda o per Pier
  // il piano restava vuoto
  const list = all.filter((a) => (!aud || a.auditorId === aud || a.managerId === aud) && (!type || a.type === type) && (!ps || planState(a) === ps));
  const cnt = (k: string) => list.filter((a) => planState(a) === k).length;
  // totale dell'anno diviso per origine: audit da programma (Brandart) e audit dei clienti (AUD-F8.1)
  const byClient = list.filter((a) => a.origin === "client").length;
  const cov = plan.data.coverage.filter((r) => !r.phaseOut);
  const missing = cov.reduce((s, r) => s + r.missing, 0);
  const okN = cov.filter((r) => !r.missing).length;
  const initials = (id: string) =>
    people
      .name(id)
      .split(/\s+/)
      .map((w) => w[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  const v = cfg.data?.visits;
  const defMonth = String(Math.min(12, Math.max(1, (dayjs().year() === year ? dayjs().month() : 0) + 2))).padStart(2, "0");

  const coverageInfo = (
    <span style={{ fontSize: 12, opacity: 0.7 }}>
      {okN}/{cov.length} {t("a_suppliersCovered")} · {missing} {t("a_visitsMissingLower")}
      {v && ` · ${t("a_rule")}: ${t("risk_high")} ${v.high}, ${t("risk_medium")} ${v.medium}, ${t("risk_low")} ${v.low}`}
    </span>
  );

  return (
    <div>
      <PageHead
        title={
          <Space size={8} wrap align="center">
            <span>{t("plan")}</span>
            <Space.Compact>
              <Tooltip title={t("y_prev")}><Button icon={<LeftOutlined />} onClick={() => setYear(year - 1)} aria-label={t("y_prev")} /></Tooltip>
              <Select style={{ width: 96 }} value={year} onChange={setYear} options={years.map((y) => ({ value: y, label: String(y) }))} aria-label={t("plan")} />
              <Tooltip title={t("y_next")}><Button icon={<RightOutlined />} onClick={() => setYear(year + 1)} aria-label={t("y_next")} /></Tooltip>
            </Space.Compact>
            {year !== thisYear && <Button type="link" onClick={() => setYear(thisYear)}>{t("y_current")}</Button>}
          </Space>
        }
        actions={
          <PageActions
            items={[
              { key: "new", label: t("newAudit"), icon: <PlusOutlined />, primary: true, hidden: !canCreate, onClick: () => setForm({}) },
              { key: "imp", label: t("importXlsx"), icon: <UploadOutlined />, hidden: !canCreate, onClick: () => setImp(true) },
              { key: "exp", label: t("exportPlan"), icon: <DownloadOutlined />, onClick: () => void download("/plan/export.xlsx", { year }) },
            ]}
          />
        }
      />
      <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t("a_planInternal")} />
      <FilterBar active={[aud, type, ps].filter(Boolean).length} onReset={() => { setAud(undefined); setType(undefined); setPs(undefined); }} >
        <Select allowClear showSearch optionFilterProp="label" placeholder={t("pl_person")} style={{ width: 230 }} value={aud} onChange={setAud} options={people.options(["auditor", "manager", "admin"])} />
        <Select allowClear placeholder={t("auditType")} style={{ width: 170 }} value={type} onChange={setType} options={AUDIT_TYPES.map((x) => ({ value: x, label: t(`t_${x}`) }))} />
        <Select allowClear placeholder={t("status")} style={{ width: 170 }} value={ps} onChange={setPs} options={["planned", "exec", "done"].map((x) => ({ value: x, label: t(`ps_${x}`) }))} />
      </FilterBar>
      <div style={kpiGrid} className={kpiGridClass}>
        {["planned", "exec", "done"].map((k) => (
          <Kpi key={k} value={cnt(k)} label={t(`ps_${k}`)} />
        ))}
        <Kpi value={list.length} label={t("ps_total")} sub={`${t("ps_brandart")} ${list.length - byClient} · ${t("ps_clients")} ${byClient}`} />
        {showRisk && <Kpi value={missing} label={t("a_visitsMissing")} tone={missing ? "alert" : "good"} />}
      </div>
      <PlanLegend />
      <PlanCalendar year={year} audits={list} canCreate={canCreate} initials={initials} onOpen={(a: AuditDto) => nav(`/audits/${a.id}`)} onAdd={(date) => setForm({ date })} />

      {showRisk && (
        <Card
          style={{ marginTop: 16 }}
          styles={{ body: { padding: 0 } }}
          title={`${t("coverage")} · ${year}`}
          extra={mobile ? null : coverageInfo}
        >
          {mobile && <div style={{ padding: "10px 14px 0" }}>{coverageInfo}</div>}
          <CoverageTable rows={plan.data.coverage} canCreate={canCreate} onPlan={(supplierId) => setForm({ supplierId, date: `${year}-${defMonth}-01` })} />
        </Card>
      )}

      {form && <AuditFormModal open onClose={() => setForm(null)} date={form.date} supplierId={form.supplierId} />}
      <ImportPlanModal open={imp} onClose={() => setImp(false)} />
    </div>
  );
}
